import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DAY_PART_WEEK_INDEX, HOURS_IN_DAY } from '../../constants';
import { formatHourForDisplay, type ServiceDisplayDayPart } from '../../day-part/utils';
import { useDayPartsLocale } from '../../hooks/useDayPartLocale';
import { DayPartScheduleViewContent } from './DayPartScheduleViewContent';

interface DayPartScheduleViewProps {
  dayPartIndex: number;
  sidebarOpen: boolean;
  dayPartData: ServiceDisplayDayPart[];
  onEdit: (id: string) => void;
  onNavigateDay?: (offset: number) => void;
  onDeleteDayPart?: (id: string) => void;
}

export const DayPartScheduleView = (props: DayPartScheduleViewProps) => {
  const { dayPartIndex, sidebarOpen, dayPartData, onEdit, onNavigateDay, onDeleteDayPart } = props;
  const isWeekView = dayPartIndex === DAY_PART_WEEK_INDEX;
  const dayParts = useDayPartsLocale();

  return (
    <div className="grid grid-rows-[auto_1fr]">
      {sidebarOpen &&
        (isWeekView ? (
          <div
            id="sidebar-schedule-view-header"
            className="bg-base sticky top-0 z-10 inline-flex bg-background-surface"
          >
            {[...Array(DAY_PART_WEEK_INDEX)].map((_, index) => (
              <h1
                key={index}
                className={cn(
                  index === 0 && 'ml-14',
                  'h-[60px] w-[74px] py-4 text-center align-middle text-base font-semibold leading-5 tracking-tight text-foreground',
                )}
              >
                {dayParts[index]?.slice(0, 3) ?? ''}
              </h1>
            ))}
          </div>
        ) : (
          <div
            id="sidebar-schedule-view-header"
            className="sticky top-0 z-10 flex w-full items-center justify-between bg-background-surface px-4"
          >
            <ChevronLeft
              className="size-5 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={() => onNavigateDay?.(-1)}
            />
            <h1 className="h-[60px] py-4 text-center align-middle text-base font-semibold leading-5 tracking-tight text-foreground">
              {dayParts[dayPartIndex]}
            </h1>
            <ChevronRight
              className="size-5 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={() => onNavigateDay?.(1)}
            />
          </div>
        ))}
      <div className="flex pb-[80px]">
        <div className={cn('flex-shrink-0', isWeekView && !sidebarOpen ? 'w-[1px]' : 'w-[36px]')}>
          <TimeLabelsColumn isWeekView={isWeekView} sidebarOpen={sidebarOpen} />
        </div>
        {isWeekView ? (
          <div className="ml-3 flex overflow-x-auto">
            {[...Array(DAY_PART_WEEK_INDEX)].map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex-shrink-0',
                  sidebarOpen ? 'mt-3 w-[74px] border-r border-border-muted' : 'w-[1px]',
                )}
              >
                <DayPartScheduleViewContent
                  dayIndex={index}
                  isWeekView={true}
                  sidebarOpen={sidebarOpen}
                  dayPartData={dayPartData}
                  onEdit={onEdit}
                  onDeleteDayPart={onDeleteDayPart}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className={cn(sidebarOpen && 'ml-3 mt-3 w-full')}>
            <DayPartScheduleViewContent
              dayIndex={dayPartIndex}
              isWeekView={false}
              sidebarOpen={sidebarOpen}
              dayPartData={dayPartData}
              onEdit={onEdit}
              onDeleteDayPart={onDeleteDayPart}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface DayPartsGridLineProps {
  isWeekView: boolean;
  sidebarOpen: boolean;
}

const TimeLabelsColumn = (props: DayPartsGridLineProps) => {
  const { isWeekView, sidebarOpen } = props;
  const width = isWeekView && !sidebarOpen ? 'w-[1px]' : sidebarOpen ? 'w-full' : 'w-[36px]';

  return (
    <div className={cn('mt-1 grid', width)}>
      {sidebarOpen &&
        [...Array(HOURS_IN_DAY)].map((_, index) => (
          <div key={index} className="flex h-[60px] justify-end">
            <span
              id={`time-label-${index}`}
              className="text-right align-middle font-body text-xs font-medium leading-tight tracking-normal"
            >
              {formatHourForDisplay(index)}
            </span>
          </div>
        ))}
    </div>
  );
};
